import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';

import { appConfig } from '../../config/app';
import { CustomEvents } from '../../constants/analytics.constant';
import { useAuthDataContext } from '../../context/auth/auth.provider';
import useLogEventInAnalytics from '../../hooks/useLogAnalyticsEvent';
import Documentation from '../../screens/PrivateLayout/SelfInspection/Steps/Documentation.component';
import {
  getSelfInspectionsAPI,
  sendSelfInspectionAPI,
  validatePhotoAPI,
} from '../../services/selfInspection/selfInspection.service';
import SelfInspectionReducer, {
  FAILURE_SELF_INSPECTIONS,
  FAILURE_SEND_SELF_INSPECTION,
  FAILURE_VALIDATE_PHOTO,
  REQUEST_SELF_INSPECTIONS,
  REQUEST_SEND_SELF_INSPECTION,
  REQUEST_VALIDATE_PHOTO,
  RESET_ALL,
  RESET_PART,
  SUCCESS_SELF_INSPECTIONS,
  SUCCESS_SEND_SELF_INSPECTION,
  SUCCESS_VALIDATE_PHOTO,
} from './selfInspection.reducer';

export const SelfInspectionDataContext = createContext<any>(null);

const initialValues = {
  success: {},
  errors: {},
  isFleet: false,
  previousInspections: [],
  selfInspections: [],
  loading: false,
};

const SelfInspectionDataProvider = ({ ...props }) => {
  const [, setCachedStep] = useState(0);
  const [step, setStep] = useState(0);
  const [state, dispatch] = useReducer(SelfInspectionReducer, initialValues);
  const [selectedInspection, setSelectedInspection] = useState<any>({});
  const [steps, setSteps] = useState<any>([
    {
      title: 'AutoInspección',
      content: <Documentation />,
    },
  ]);
  const history = useHistory();
  const { logEvent } = useLogEventInAnalytics();

  const { embed } = useAuthDataContext();
  const redirectHome = () => {
    embed
      ? window.parent.postMessage({ return: 'overview' }, '*')
      : history.push('/dashboard');
  };

  const goBack = () => {
    if (step !== steps.length - 1) {
      setStep(step - 1);
    } else {
      redirectHome();
      reset();
    }
  };

  const nextStep = () => {
    dispatch({
      type: RESET_PART,
    });

    setCachedStep(step);

    const nextIndex = steps.findIndex((step: any) => {
      return step.part && !step.part?.photo && !step.part.skipped;
    });

    if (nextIndex !== -1 && nextIndex !== 0) {
      setStep(nextIndex);
    } else {
      const current = steps
        .map(
          (step: any) =>
            !!(step.part && (step.part.skipped ? true : !!step.part?.photo))
        )
        .lastIndexOf(true);

      if (current !== -1) {
        setStep(current + 1);
      } else {
        setStep(step + 1);
      }
    }
  };

  const getSelfInspections = async () => {
    dispatch({
      type: REQUEST_SELF_INSPECTIONS,
    });

    try {
      const res = await getSelfInspectionsAPI();

      await logEvent(CustomEvents.SELF_INSPECTION_REQUEST, {
        app: appConfig?.NAME,
      });

      dispatch({
        type: SUCCESS_SELF_INSPECTIONS,
        payload: res.data,
      });
    } catch (err) {
      const data = {
        ...err.response?.data,
        status: err.response?.status,
      };

      await logEvent(CustomEvents.SELF_INSPECTION_ERROR, {
        app: appConfig?.NAME,
        error: {
          name: CustomEvents.SELF_INSPECTION_REQUEST,
          body: err.response,
        },
      });
      dispatch({
        type: FAILURE_SELF_INSPECTIONS,
        payload: data,
      });
    }
  };

  const setPart = (part: any) => {
    const index = steps.findIndex((step: any) => step.part?.id === part.id);

    const newSteps = steps;
    newSteps[index].part = part;

    setSteps(newSteps);
  };

  const validatePhoto = async (body: any, partInfo: any) => {
    dispatch({
      type: REQUEST_VALIDATE_PHOTO,
    });

    // dispatch({
    //   type: SET_PART,
    //   payload: body
    // })

    setPart(partInfo);

    try {
      const res = await validatePhotoAPI(selectedInspection.id, body);

      await logEvent(CustomEvents.SELF_INSPECTION_VALIDATE, {
        app: appConfig?.NAME,
      });

      dispatch({
        type: SUCCESS_VALIDATE_PHOTO,
        payload: res.data,
      });
    } catch (err) {
      const data = {
        ...err.response?.data,
        status: err.response?.status,
      };
      await logEvent(CustomEvents.SELF_INSPECTION_ERROR, {
        app: appConfig?.NAME,
        error: {
          name: CustomEvents.SELF_INSPECTION_VALIDATE,
          body: err.response,
        },
      });
      dispatch({
        type: FAILURE_VALIDATE_PHOTO,
        payload: data,
      });

      // dispatch({
      //   type: DELETE_PART,
      //   payload: body
      // })
    }
  };

  const sendSelfInspection = async (body: any) => {
    // console.log(steps.map((step: any) => step.part).filter((part: any) => !!part))
    dispatch({
      type: REQUEST_SEND_SELF_INSPECTION,
    });

    try {
      const res = await sendSelfInspectionAPI(selectedInspection.id);

      await logEvent(CustomEvents.SELF_INSPECTION_FINISHED, {
        app: appConfig?.NAME,
      });

      dispatch({
        type: SUCCESS_SEND_SELF_INSPECTION,
        payload: res.data,
      });

      getSelfInspections();
    } catch (err) {
      const data = {
        ...err.response?.data,
        status: err.response?.status,
      };
      await logEvent(CustomEvents.SELF_INSPECTION_ERROR, {
        app: appConfig?.NAME,
        error: {
          name: CustomEvents.SELF_INSPECTION_FINISHED,
          body: err.response,
        },
      });
      dispatch({
        type: FAILURE_SEND_SELF_INSPECTION,
        payload: data,
      });
    }
  };

  const reset = async () => {
    dispatch({
      type: RESET_ALL,
      payload: selectedInspection,
    });
    setCachedStep(0);
    setStep(0);
    setSelectedInspection({});
    setSteps([
      {
        title: 'AutoInspección',
        content: <Documentation />,
      },
    ]);
    await logEvent(CustomEvents.SELF_INSPECTION_UNFINISHED, {
      app: appConfig?.NAME,
    });
  };

  const SelfInspectionDataValue = useMemo(
    () => ({
      ...state,
      steps,
      setSteps,
      step,
      setStep,
      selectedInspection,
      setSelectedInspection,
      goBack,
      nextStep,
      reset,
      getSelfInspections,
      validatePhoto,
      sendSelfInspection,
      dispatch,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step, state]
  );

  return (
    <SelfInspectionDataContext.Provider
      value={SelfInspectionDataValue}
      {...props}
    />
  );
};

export const useSelfInspectionDataContext = () =>
  useContext(SelfInspectionDataContext);

export default SelfInspectionDataProvider;
